import React, { useState, useMemo, useRef } from 'react';
import {
    PAYMENT_TYPES,
    BILLING_CYCLES,
    CURRENCIES,
    PAYMENT_METHODS,
    REMINDER_OFFSETS,
    offsetLabel,
    formatAmount,
    formatDateLong,
    formatDateTime,
    toDateInput,
    toDateTimeInput,
    fromDateTimeInput,
    advanceDueDate,
    cardLabel,
    costCentreId
} from './paymentConstants';
import { SERVICE_GROUPS, searchServices, findService } from './serviceCatalog';
import { CardPicker, CostCentreSelect } from './PaymentPickers';
import ServiceAvatar from './ServiceAvatar';

// Adding a service is "which one?", then only the fields that kind needs:
//
//   Step 1  Which service?          → pick Meta Ads / Claude / Hostinger ... (one click)
//                                     or "Something else" → what kind of payment?
//   Step 2  Amount, card, branch, and the dates that kind needs
//
// Picking a known service fills the name, vendor, type, cycle, category and the
// vendor's billing page, so the common case is: amount, date, Save. Card and
// branch default to the last-used card and the user's own branch. Editing skips
// straight to step 2.

const emptyForm = {
    name: '',
    vendor: '',
    description: '',
    category: '',
    amount: '',
    currency: 'QAR',
    billingCycle: 'monthly',
    nextDueDate: '',
    startDate: '',
    paidDate: '',
    reference: '',
    autoRenew: true,
    isTrial: false,
    accountEmail: '',
    reminderOffsets: [7, 3, 1],
    reviewFrequencyDays: 30,
    notifyEmails: '',
    paymentMethod: '',
    card: '',
    costCentre: '',
    serviceKey: '',
    billingUrl: '',
    notes: '',
    status: 'active'
};

const datePresets = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const firstNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    const inAMonth = new Date(today);
    inAMonth.setMonth(inAMonth.getMonth() + 1);
    const inAYear = new Date(today);
    inAYear.setFullYear(inAYear.getFullYear() + 1);
    return [
        { label: 'In a month', value: toDateInput(inAMonth) },
        { label: '1st next month', value: toDateInput(firstNextMonth) },
        { label: 'In a year', value: toDateInput(inAYear) },
        { label: 'Tomorrow', value: toDateInput(tomorrow) }
    ];
};

const PaymentFormWizard = ({
    editing, categories, lookups, defaultCardId, defaultEmail,
    saving, error, onSubmit, onClose, onCardCreated
}) => {
    const othersCategory = useMemo(
        () => categories.find(c => c.name.toLowerCase() === 'others') || categories[categories.length - 1],
        [categories]
    );
    const cards = lookups?.cards || [];
    const costCentres = lookups?.costCentres || [];

    const [step, setStep] = useState(editing ? 'details' : 'service');
    const [paymentType, setPaymentType] = useState(editing ? (editing.paymentType || 'subscription') : null);
    const [serviceSearch, setServiceSearch] = useState('');
    const cardPickerRef = useRef(null);
    const [form, setForm] = useState(() => {
        if (!editing) {
            return {
                ...emptyForm,
                notifyEmails: defaultEmail || '',
                paidDate: toDateTimeInput(new Date()),
                card: defaultCardId || '',
                costCentre: lookups?.myCostCentre ? String(lookups.myCostCentre) : ''
            };
        }
        const editingCard = String(editing.card?._id || editing.card || '');
        return {
            ...emptyForm,
            name: editing.name || '',
            vendor: editing.vendor || '',
            description: editing.description || '',
            category: editing.category?._id || editing.category || '',
            amount: editing.amount ?? '',
            currency: editing.currency || 'QAR',
            billingCycle: editing.billingCycle && editing.billingCycle !== 'none' ? editing.billingCycle : 'monthly',
            nextDueDate: toDateInput(editing.nextDueDate),
            startDate: toDateInput(editing.startDate),
            autoRenew: editing.autoRenew !== false,
            isTrial: editing.status === 'trial',
            accountEmail: editing.accountEmail || '',
            reminderOffsets: editing.reminderOffsets?.length
                ? editing.reminderOffsets
                : [editing.reminderDaysBefore ?? 5],
            reviewFrequencyDays: editing.reviewFrequencyDays ?? 30,
            notifyEmails: (editing.notifyEmails || []).join(', '),
            // A legacy record has a typed method and no card; keep the text
            paymentMethod: editingCard ? '' : (editing.paymentMethod || ''),
            card: editingCard,
            costCentre: costCentreId(editing),
            serviceKey: editing.serviceKey || '',
            billingUrl: editing.billingUrl || '',
            notes: editing.notes || '',
            status: editing.status === 'paused' ? 'paused' : 'active'
        };
    });
    const [showMore, setShowMore] = useState(false);

    const isRecurring = paymentType === 'subscription';
    const isOneTime = paymentType === 'onetime';
    const categoryId = form.category || othersCategory?._id || '';

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const toggleOffset = (offset) => {
        setForm(prev => {
            const has = prev.reminderOffsets.includes(offset);
            const next = has
                ? prev.reminderOffsets.filter(o => o !== offset)
                : [...prev.reminderOffsets, offset];
            return { ...prev, reminderOffsets: next.sort((a, b) => b - a) };
        });
    };

    const chooseService = (service) => {
        setPaymentType(service.paymentType);
        setForm(prev => ({
            ...prev,
            name: service.name,
            vendor: service.vendor || '',
            serviceKey: service.key,
            billingUrl: service.billingUrl || '',
            billingCycle: service.billingCycle || prev.billingCycle,
            category: categories.find(c => c.name.toLowerCase() === (service.category || '').toLowerCase())?._id
                || prev.category || othersCategory?._id || ''
        }));
        setStep('details');
    };

    const chooseType = (type) => {
        setPaymentType(type);
        if (!form.category && othersCategory) set('category', othersCategory._id);
        setStep('details');
    };

    const selectedCard = cards.find(c => String(c._id) === String(form.card));
    const selectedCentre = costCentres.find(t => String(t._id) === String(form.costCentre));

    // Live plain-English description of what is about to be saved, so a
    // misconfigured schedule is obvious before it is stored rather than after
    // the first surprise charge.
    const summary = useMemo(() => {
        const categoryName = categories.find(c => c._id === categoryId)?.name;
        const paidWith = selectedCard ? ` on ${cardLabel(selectedCard)}` : (form.paymentMethod ? ` by ${form.paymentMethod}` : '');
        const chargedTo = selectedCentre ? `, charged to ${selectedCentre.name}` : '';

        if (isOneTime) {
            if (!form.amount) return 'Fill in the amount to see a summary.';
            return `${formatAmount(form.amount, form.currency)} paid once${paidWith} on ${formatDateTime(form.paidDate || new Date())}${chargedTo}${categoryName ? `, filed under ${categoryName}` : ''}. It will not renew and will never appear in upcoming payments.`;
        }

        if (!isRecurring) {
            const estimate = form.amount ? ` Typical charge is around ${formatAmount(form.amount, form.currency)}.` : '';
            return `No fixed schedule and no reminders${paidWith ? ` — charged${paidWith}` : ''}${chargedTo}.${estimate} Use "Record charge" each time the card is billed.`;
        }

        if (!form.amount || !form.nextDueDate) {
            return 'Fill in the amount and the renewal date to see a summary.';
        }

        const cycle = BILLING_CYCLES.find(c => c.value === form.billingCycle);
        const every = form.billingCycle === 'variable'
            ? `every ${form.reviewFrequencyDays || 30} days`
            : `every ${cycle?.shortLabel || 'month'}`;

        const first = new Date(form.nextDueDate);
        const upcoming = [];
        let cursor = first;
        for (let i = 0; i < 2; i++) {
            cursor = advanceDueDate(cursor, form.billingCycle, first.getDate(), form.reviewFrequencyDays);
            if (!cursor) break;
            upcoming.push(cursor.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }));
        }

        const emails = form.notifyEmails.split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
        const reminderPart = form.reminderOffsets.length === 0 || emails.length === 0
            ? ' No reminders will be sent.'
            : ` Reminders ${form.reminderOffsets.map(offsetLabel).join(', ').toLowerCase()} before, to ${emails.length === 1 ? emails[0] : `${emails.length} people`}.`;

        const renewPart = form.autoRenew
            ? ` Auto-renewal is ON — it charges${paidWith || ' the card'} by itself.`
            : ' Auto-renewal is OFF — someone must pay it manually.';

        const trialPart = form.isTrial
            ? `Free trial ends ${formatDateLong(first)}.${form.autoRenew ? ' It will most likely convert to a paid subscription then.' : ''} After that: `
            : '';

        return `${trialPart}${formatAmount(form.amount, form.currency)} ${every}${chargedTo}. Next ${formatDateLong(first)}${upcoming.length ? `, then ${upcoming.join(', ')}` : ''}.${renewPart}${reminderPart}`;
    }, [isRecurring, isOneTime, form, categories, categoryId, selectedCard, selectedCentre]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        // A card typed into the add-card form but not yet added is added now;
        // if that fails, its form shows why and nothing is saved
        const pendingCard = await cardPickerRef.current?.commitPending();
        if (pendingCard === null) return;
        const card = pendingCard ?? form.card;
        onSubmit({
            ...form,
            card,
            category: categoryId,
            paymentType,
            // An absolute instant, so the server (on UTC) stores the right time
            paidDate: isOneTime ? fromDateTimeInput(form.paidDate) : undefined,
            paymentMethod: card ? '' : form.paymentMethod,
            ...(isRecurring ? {} : { nextDueDate: undefined, notifyEmails: undefined })
        });
    };

    // ---- Step 1: which service ----
    if (step === 'service') {
        const results = searchServices(serviceSearch);
        const grouped = SERVICE_GROUPS
            .map(group => ({ group, items: results.filter(s => s.group === group) }))
            .filter(g => g.items.length > 0);

        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Add a service</h2>
                        <button type="button" className="modal-close" onClick={onClose}>×</button>
                    </div>
                    <div className="modal-body">
                        <p className="step-question">Which service do you pay for?</p>
                        <input
                            className="search-input service-search"
                            type="search"
                            placeholder="Search — Meta, Claude, Hostinger, Play Store, SerpApi..."
                            value={serviceSearch}
                            onChange={(e) => setServiceSearch(e.target.value)}
                            autoFocus
                        />
                        {grouped.map(({ group, items }) => (
                            <div key={group} className="service-group">
                                <div className="service-group-label">{group}</div>
                                <div className="service-grid">
                                    {items.map(service => (
                                        <button
                                            key={service.key}
                                            type="button"
                                            className="service-tile"
                                            onClick={() => chooseService(service)}
                                            title={service.hint || service.name}
                                        >
                                            <ServiceAvatar record={{ serviceKey: service.key }} />
                                            <span className="service-tile-text">
                                                <span className="service-tile-name">{service.name}</span>
                                                <span className="service-tile-type">
                                                    {PAYMENT_TYPES[service.paymentType].icon} {PAYMENT_TYPES[service.paymentType].label}
                                                    {service.billingCycle && service.paymentType === 'subscription'
                                                        ? ` · ${BILLING_CYCLES.find(c => c.value === service.billingCycle)?.label}`
                                                        : ''}
                                                </span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))}
                        {grouped.length === 0 && (
                            <p className="form-hint centered-hint">Not in the list — use "Something else" below.</p>
                        )}
                        <button type="button" className="service-tile service-tile-other" onClick={() => setStep('type')}>
                            <span className="pay-card-icon" aria-hidden="true">＋</span>
                            <span className="service-tile-text">
                                <span className="service-tile-name">Something else</span>
                                <span className="service-tile-type">Any other subscription, licence or bill</span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ---- Step 1b: which kind (for "Something else", or changing type) ----
    if (step === 'type' || !paymentType) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>{editing ? `Change type — ${editing.name}` : 'Add a service'}</h2>
                        <button type="button" className="modal-close" onClick={onClose}>×</button>
                    </div>
                    <div className="modal-body">
                        <p className="step-question">How is it charged?</p>
                        {editing && (
                            <p className="form-hint centered-hint">
                                Switching away from Recurring removes the renewal date and stops reminders.
                            </p>
                        )}
                        <div className="type-choice-grid">
                            {Object.values(PAYMENT_TYPES).map(type => (
                                <button
                                    key={type.value}
                                    type="button"
                                    className="type-choice"
                                    onClick={() => chooseType(type.value)}
                                >
                                    <span className="type-choice-icon">{type.icon}</span>
                                    <span className="type-choice-title">{type.label}</span>
                                    <span className="type-choice-blurb">{type.blurb}</span>
                                    <span className="type-choice-examples">{type.examples}</span>
                                </button>
                            ))}
                        </div>
                        {!editing && (
                            <div className="centered-hint">
                                <button type="button" className="link-button" onClick={() => setStep('service')}>← Back to the service list</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ---- Step 2: the details that kind needs ----
    const typeMeta = PAYMENT_TYPES[paymentType];
    const service = findService(form.serviceKey);

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{editing ? `Edit — ${editing.name}` : `Add ${form.name || typeMeta.label}`}</h2>
                    <button type="button" className="modal-close" onClick={onClose}>×</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="modal-body">
                        {error && <div className="alert alert-error">{error}</div>}

                        <div className="type-chip-row">
                            <ServiceAvatar record={{ serviceKey: form.serviceKey, name: form.name, vendor: form.vendor, paymentType }} size="sm" />
                            <span className="type-chip">{typeMeta.icon} {typeMeta.label}</span>
                            {service?.hint && <span className="form-hint">{service.hint}</span>}
                            <button type="button" className="link-button" onClick={() => setStep('type')}>
                                Change type
                            </button>
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Service name *</label>
                                <input
                                    className="form-input"
                                    value={form.name}
                                    onChange={(e) => set('name', e.target.value)}
                                    placeholder={isRecurring ? 'e.g. Claude Pro' : isOneTime ? 'e.g. Play Console registration' : 'e.g. Meta Ads'}
                                    autoFocus={!service}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">
                                    {isRecurring ? 'Amount each time *' : isOneTime ? 'Amount paid *' : 'Typical charge'}
                                    {!isRecurring && !isOneTime && <span className="form-hint"> (optional)</span>}
                                </label>
                                <div className="amount-row">
                                    <input
                                        className="form-input"
                                        type="number" min="0" step="0.01"
                                        value={form.amount}
                                        onChange={(e) => set('amount', e.target.value)}
                                        placeholder="0.00"
                                        autoFocus={Boolean(service)}
                                        required={isRecurring || isOneTime}
                                    />
                                    <select className="form-select currency-select" value={form.currency} onChange={(e) => set('currency', e.target.value)}>
                                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <span className="form-hint">As it appears on the card statement</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Paid with</label>
                            <CardPicker
                                ref={cardPickerRef}
                                cards={cards}
                                value={form.card}
                                onChange={(id) => set('card', id)}
                                onCardCreated={onCardCreated}
                            />
                            {!form.card && (
                                <>
                                    <input
                                        className="form-input"
                                        list="payment-methods"
                                        value={form.paymentMethod}
                                        onChange={(e) => set('paymentMethod', e.target.value)}
                                        placeholder="e.g. Bank Transfer"
                                    />
                                    <datalist id="payment-methods">
                                        {PAYMENT_METHODS.map(m => <option key={m} value={m} />)}
                                    </datalist>
                                </>
                            )}
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Charged to (company / branch)</label>
                                <CostCentreSelect
                                    costCentres={costCentres}
                                    value={form.costCentre}
                                    onChange={(id) => set('costCentre', id)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Category</label>
                                <select className="form-select" value={categoryId} onChange={(e) => set('category', e.target.value)}>
                                    {categories.map(c => (
                                        <option key={c._id} value={c._id}>{c.icon} {c.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {isOneTime && (
                            <div className="form-grid">
                                <div className="form-group">
                                    <label className="form-label">Paid on *</label>
                                    <input
                                        className="form-input" type="datetime-local" required
                                        value={form.paidDate}
                                        onChange={(e) => set('paidDate', e.target.value)}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Reference</label>
                                    <input
                                        className="form-input"
                                        value={form.reference}
                                        placeholder="Invoice / transaction no."
                                        onChange={(e) => set('reference', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {isRecurring && (
                            <>
                                <div className="form-group">
                                    <label className="form-label">How often</label>
                                    <div className="chip-row">
                                        {BILLING_CYCLES.map(cycle => (
                                            <button
                                                key={cycle.value}
                                                type="button"
                                                className={`chip ${form.billingCycle === cycle.value ? 'chip-active' : ''}`}
                                                onClick={() => set('billingCycle', cycle.value)}
                                            >
                                                {cycle.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {form.billingCycle === 'variable' && (
                                    <div className="form-group">
                                        <label className="form-label">
                                            Every how many days? <span className="form-hint">(counted from each payment)</span>
                                        </label>
                                        <input
                                            className="form-input short-input" type="number" min="1" max="365"
                                            value={form.reviewFrequencyDays}
                                            onChange={(e) => set('reviewFrequencyDays', e.target.value)}
                                        />
                                    </div>
                                )}

                                <div className="form-group">
                                    <label className="form-label">
                                        {form.isTrial ? 'Trial ends *' : 'Next renewal date *'}
                                    </label>
                                    <input
                                        className="form-input short-input" type="date" required
                                        value={form.nextDueDate}
                                        onChange={(e) => set('nextDueDate', e.target.value)}
                                    />
                                    <div className="chip-row chip-row-tight">
                                        {datePresets().map(preset => (
                                            <button key={preset.label} type="button" className="chip chip-sm" onClick={() => set('nextDueDate', preset.value)}>
                                                {preset.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Auto-renewal decides whether money leaves by itself.
                                    It gets a full-width switch rather than a checkbox
                                    buried among the optional fields. */}
                                <div className={`switch-row ${form.autoRenew ? 'switch-on' : 'switch-off'}`}>
                                    <label className="switch-control">
                                        <input
                                            type="checkbox"
                                            checked={form.autoRenew}
                                            onChange={(e) => set('autoRenew', e.target.checked)}
                                        />
                                        <span className="switch-title">
                                            Auto-renewal is {form.autoRenew ? 'ON' : 'OFF'}
                                        </span>
                                    </label>
                                    <span className="switch-blurb">
                                        {form.autoRenew
                                            ? 'The card is charged automatically on the renewal date — you just confirm it afterwards.'
                                            : 'This will not renew by itself — someone has to pay it, or the service lapses.'}
                                    </span>
                                </div>

                                <label className="checkbox-row">
                                    <input
                                        type="checkbox"
                                        checked={form.isTrial}
                                        onChange={(e) => set('isTrial', e.target.checked)}
                                    />
                                    <span>
                                        This is a <strong>free trial</strong>
                                        <span className="form-hint"> — we'll warn you before it converts to a paid plan</span>
                                    </span>
                                </label>

                                <div className="form-group">
                                    <label className="form-label">Remind these people *</label>
                                    <input
                                        className="form-input"
                                        value={form.notifyEmails}
                                        onChange={(e) => set('notifyEmails', e.target.value)}
                                        placeholder="accounts@company.com, manager@company.com"
                                        required
                                    />
                                    <div className="chip-row chip-row-tight">
                                        <span className="template-label">Remind:</span>
                                        {REMINDER_OFFSETS.map(offset => (
                                            <button
                                                key={offset}
                                                type="button"
                                                className={`chip chip-sm ${form.reminderOffsets.includes(offset) ? 'chip-active' : ''}`}
                                                onClick={() => toggleOffset(offset)}
                                            >
                                                {offsetLabel(offset)}
                                            </button>
                                        ))}
                                        <span className="form-hint">before</span>
                                    </div>
                                </div>
                            </>
                        )}

                        <button type="button" className="disclosure-toggle" onClick={() => setShowMore(v => !v)}>
                            {showMore ? '▾' : '▸'} More details
                            <span className="form-hint"> (billing page, account login, vendor, notes)</span>
                        </button>

                        {showMore && (
                            <div className="disclosure-body">
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label className="form-label">Billing page</label>
                                        <input
                                            className="form-input"
                                            value={form.billingUrl}
                                            onChange={(e) => set('billingUrl', e.target.value)}
                                            placeholder="https://… where invoices and the card are managed"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Account / login email</label>
                                        <input
                                            className="form-input" type="email"
                                            value={form.accountEmail}
                                            onChange={(e) => set('accountEmail', e.target.value)}
                                            placeholder="which account is this under?"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Vendor</label>
                                        <input className="form-input" value={form.vendor} onChange={(e) => set('vendor', e.target.value)} placeholder="e.g. OpenAI" />
                                    </div>
                                    {isRecurring && (
                                        <div className="form-group">
                                            <label className="form-label">Started on</label>
                                            <input className="form-input" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
                                        </div>
                                    )}
                                    {editing && isRecurring && (
                                        <div className="form-group">
                                            <label className="form-label">Status</label>
                                            <select className="form-select" value={form.status} onChange={(e) => set('status', e.target.value)}>
                                                <option value="active">Active</option>
                                                <option value="paused">Paused (stop reminders)</option>
                                            </select>
                                        </div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Description</label>
                                    <input className="form-input" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What is this for? e.g. MOCI server" />
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Notes</label>
                                    <textarea className="form-textarea" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
                                </div>
                            </div>
                        )}

                        <div className="summary-box">
                            <span className="summary-box-icon">{typeMeta.icon}</span>
                            <span>{summary}</span>
                        </div>
                    </div>

                    <div className="modal-footer">
                        {!editing && (
                            <button type="button" className="btn btn-secondary" onClick={() => setStep('service')}>← Back</button>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Saving...' : (editing ? 'Save changes' : 'Save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PaymentFormWizard;
